import _extends from "@babel/runtime/helpers/extends";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { calculateRem } from '@uie/javascript-style-helpers';
import { BodyConfig, TypographyConfig } from '@vds-core/typography';
import { colors } from '@vds-core/theme';
import { withVDSManager } from '@vds-core/utilities';
var propTypes = {
  /**
   * Determines display layout of button.
   */
  type: PropTypes.oneOf(['inline', 'standAlone']),

  /**
   * @ignore
   * Determines viewport of button. Desktop and tablet are the same size.
   */
  viewport: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),

  /**
   * String that is used to render as the text link.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  /**
   * TextLink will be disabled if disabled prop is passed.
   */
  disabled: PropTypes.bool,

  /**
   * TextLink will be displayed in Inverted colors if inverted prop is passed.
   */
  inverted: PropTypes.bool,

  /**
   *	This function will be called when the TextLink is clicked. It will return an event.
   */
  onClick: PropTypes.func,

  /**
   *	This function will be called when keydown event fires on TextLink. It will return an event.
   */
  onKeyDown: PropTypes.func,

  /**
   * This string will be applied to the href attribute.
   */
  href: PropTypes.string,

  /**
   * @ignore
   * Determines color of TextLink text.
   */
  color: PropTypes.oneOf([colors.blue, colors.black]),

  /**
   * @ignore
   * If true, TextLink text will render as bold.
   */
  bold: PropTypes.bool,

  /**
   *	If provided, used as string that labels the element for accessibility.
   */
  ariaLabel: PropTypes.string,

  /**
   * @ignore
   */
  focusState: PropTypes.bool,

  /**
   * @ignore
   */
  activeState: PropTypes.bool,

  /**
   * @ignore
   */
  hitArea: PropTypes.bool,
  //turns hit area on or off

  /**
   * @ignore
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore
   */
  colorConfig: PropTypes.object,

  /**
   * @ignore
   */
  typescale: PropTypes.string,

  /**
   * @ignore
   */
  size: PropTypes.oneOf(['small', 'large']),

  /**
   * Allows a string to be provided for analytics.
   */
  'data-analyticstrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-track': PropTypes.string,

  /**
   * Allows a string to be provided for click stream.
   */
  'data-clickstream': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-level': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-position': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-datatrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-cta': PropTypes.string,

  /**
   * @ignore
   */
  dataLoc: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * @ignore
   */
  tabIndex: PropTypes.number,

  /**
   * This will be applied to the role attribute for screen reading capabilities
   */
  role: PropTypes.oneOf(['link', 'button'])
};
var defaultProps = {
  size: undefined,
  viewport: 'desktop',
  type: 'inline',
  disabled: false,
  inverted: false,
  onClick: function onClick() {},
  bold: false,
  color: colors.primary,
  colorConfig: colors,
  typescale: TypographyConfig.getTypescale(),
  focusState: false,
  hitArea: false,
  bodyConfig: BodyConfig,
  tabIndex: 0,
  activeState: false,
  role: 'link'
};
var sharedHitAreaStyles = /*#__PURE__*/css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  left: 50%;\n  position: absolute;\n  transform: translate(-50%, -50%);\n  text-align: center;\n  box-sizing: content-box;\n  &:hover {\n    cursor: pointer;\n  }\n  top: 50%;\n  outline: none;\n"])));
var HitArea = /*#__PURE__*/styled.span.withConfig({
  displayName: "HitArea",
  componentId: "sc-9x52p1-0"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  ", ";\n  height: ", ";\n  cursor: pointer;\n  display: inline;\n  width: 100%;\n"])), sharedHitAreaStyles, function (_ref) {
  var type = _ref.type;
  return type === 'inline' ? calculateRem(24) : calculateRem(44);
});
var Wrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "Wrapper",
  componentId: "sc-9x52p1-1"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  height: ", ";\n  width: 100%;\n  position: relative;\n  &:focus {\n    outline: none;\n  }\n"])), function (_ref2) {
  var type = _ref2.type,
      bodyLarge = _ref2.bodyLarge;
  return type === 'inline' ? 'inherit' : calculateRem(bodyLarge.lineHeight);
});
var StyledAnchor = /*#__PURE__*/styled.a.withConfig({
  displayName: "StyledAnchor",
  componentId: "sc-9x52p1-2"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  width: auto;\n  position: relative;\n  border: 0;\n  cursor: pointer;\n  background-image: none;\n  background-color: transparent;\n  text-decoration: none;\n  font-size: ", ";\n  font-family: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n  height: ", ";\n  letter-spacing: ", ";\n  color: ", ";\n  border-bottom: ", " solid;\n  border-color: ", ";\n  box-sizing: border-box;\n  transition: opacity 0.15s ease-in;\n  touch-action: manipulation;\n  white-space: nowrap;\n  ", ";\n\n  ", ";\n\n  ", ";\n\n  ", ";\n\n  ", ";\n\n  ", ";\n\n  ", ";\n\n  ", "\n"])), function (_ref3) {
  var type = _ref3.type,
      bodyLarge = _ref3.bodyLarge;
  return type === 'inline' ? 'inherit' : calculateRem(bodyLarge.fontSize);
}, function (_ref4) {
  var type = _ref4.type,
      bodyLarge = _ref4.bodyLarge;
  return type === 'inline' ? 'inherit' : bodyLarge.fontFamily;
}, function (_ref5) {
  var type = _ref5.type,
      bold = _ref5.bold,
      bodyLarge = _ref5.bodyLarge;
  return bold ? '750' : type === 'inline' ? 'inherit' : bodyLarge.fontWeight;
}, function (_ref6) {
  var type = _ref6.type,
      bodyLarge = _ref6.bodyLarge;
  return type === 'inline' ? 'inherit' : calculateRem(bodyLarge.lineHeight);
}, function (_ref7) {
  var type = _ref7.type,
      bodyLarge = _ref7.bodyLarge;
  return type === 'inline' ? 'inherit' : calculateRem(bodyLarge.lineHeight);
}, function (_ref8) {
  var type = _ref8.type,
      bodyLarge = _ref8.bodyLarge;
  return type === 'inline' ? 'inherit' : bodyLarge.letterSpacing && calculateRem(bodyLarge.letterSpacing);
}, function (_ref9) {
  var color = _ref9.color;
  return color;
}, calculateRem(1), function (_ref10) {
  var color = _ref10.color;
  return color;
}, function (_ref11) {
  var focusState = _ref11.focusState,
      colors = _ref11.colors;
  return focusState && "\n     outline: none;\n     &:focus:not(:hover) {\n      &::after {\n        box-sizing: border-box;\n        border: ".concat(calculateRem(1), " dashed ").concat(colors.black, ";\n        content: '';\n        left: 50%;\n        position: absolute;\n        top: calc(50% + ").concat(calculateRem(1), ");\n        transform: translate(-50%, -50%);\n        height: calc(100% + ").concat(calculateRem(6), ");\n        width: calc(100% + ").concat(calculateRem(6), ");\n      }\n    }\n    &:hover {\n      color: ").concat(colors.blue, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.blue, ";\n    }\n    &:active {\n      color: ").concat(colors.coolGray6, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.coolGray6, ";\n    }\n    &:visited {\n      outline: none;\n    }\n  ");
}, function (_ref12) {
  var typescale = _ref12.typescale,
      colors = _ref12.colors;
  return typescale !== 'VDS' && "\n      padding: ".concat(calculateRem(0, 0, 1, 0), ";\n      &:hover,\n      &:active,\n      &:visited {\n        outline: none;\n      }\n\n      &:active {\n        opacity: 0.77;\n        transition: opacity 0.15s ease-out;\n      }\n\n      &:hover {\n        color: ").concat(colors.blue, ";\n        border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.blue, ";\n      }\n    ");
}, function (_ref13) {
  var viewport = _ref13.viewport,
      type = _ref13.type,
      bodySmall = _ref13.bodySmall,
      size = _ref13.size;
  return (viewport === 'mobile' || size === 'small') && type !== 'inline' && "\n      font-size: ".concat(calculateRem(bodySmall.fontSize), ";\n      font-family: ").concat(bodySmall.fontFamily, ";\n      font-weight: ").concat(bodySmall.fontWeight, ";\n      line-height: ").concat(calculateRem(bodySmall.lineHeight), "\n      letter-spacing: normal;\n    ");
}, function (_ref14) {
  var inverted = _ref14.inverted,
      color = _ref14.color,
      colors = _ref14.colors,
      focusState = _ref14.focusState;
  return inverted && !focusState && "\n      color: ".concat(color && color !== colors.black ? color : colors.white, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat(color && color !== colors.black ? color : colors.white, ";\n      &:active {\n        opacity: 0.77;\n        transition: opacity .15s ease-out;\n      }");
}, function (_ref15) {
  var inverted = _ref15.inverted,
      color = _ref15.color,
      colors = _ref15.colors,
      focusState = _ref15.focusState;
  return inverted && focusState && "\n      color: ".concat(color && color !== colors.black ? color : colors.white, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat(color && color !== colors.black ? color : colors.white, ";\n     &:focus:not(:hover) {\n      outline: none;\n      &::after {\n        box-sizing: border-box;\n        border: ").concat(calculateRem(1), " dashed ").concat(colors.white, ";\n        content: '';\n        left: 50%;\n        position: absolute;\n        top: calc(50% + ").concat(calculateRem(1), ");\n        transform: translate(-50%, -50%);\n        height: calc(100% + ").concat(calculateRem(6), ");\n        width: calc(100% + ").concat(calculateRem(6), ");\n      }\n    }\n      &:hover {\n        color: ").concat(colors.blueInverted, ";\n        outline: none;\n        border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.blueInverted, ";\n      }\n      &:hover {\n        color: ").concat(colors.blueInverted, ";\n        border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.blueInverted, ";\n      }\n      &:active {\n        color: ").concat(colors.coolGray6, ";\n        border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.coolGray6, ";\n      }\n      ");
}, function (_ref16) {
  var disabled = _ref16.disabled,
      colors = _ref16.colors;
  return disabled && "\n    pointer-events: none;\n    cursor: not-allowed;\n    color: ".concat(colors.coolGray3, ";\n    border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.coolGray3, ";\n    &:hover {\n      background-color: transparent;\n      color: ").concat(colors.coolGray3, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.coolGray3, ";\n    }\n    &: focus{\n      outline: none;\n    }\n  ");
}, function (_ref17) {
  var disabled = _ref17.disabled,
      inverted = _ref17.inverted;
  return disabled && inverted && "\n    border-bottom: ".concat(calculateRem(1), " solid ").concat(colors.coolGray10, ";\n    color: ").concat(colors.coolGray10, ";\n    \n  ");
}, function (_ref18) {
  var activeState = _ref18.activeState,
      color = _ref18.color;
  return activeState && "\n    color: ".concat(colors.coolGray6, " !important;\n    border-bottom: ").concat(calculateRem(1), " solid ").concat(colors.coolGray6, " !important;\n  ");
});

var TextLink = function TextLink(props) {
  var focusState = props.focusState,
      hitArea = props.hitArea,
      bodyConfig = props.bodyConfig,
      colorConfig = props.colorConfig,
      typescale = props.typescale,
      activeState = props.activeState,
      id = props.id,
      disabled = props.disabled,
      dataLoc = props.dataLoc,
      tabIndex = props.tabIndex,
      role = props.role;
  var newAriaLabel = props.ariaLabel ? props.ariaLabel : props.children && typeof props.children === 'string' ? props.children : 'Text Link';
  var smallFontSize = typescale === 'VDS' ? 'small' : 'medium';
  var largeFontSize = typescale === 'VDS' ? 'large' : 'medium';
  var bodyLarge = bodyConfig.getStyles(typescale, 'desktop', largeFontSize);
  var bodySmall = bodyConfig.getStyles(typescale, 'mobile', smallFontSize);

  var handleKeyDown = function handleKeyDown(e) {
    props.onKeyDown && props.onKeyDown(e);

    if (e.key === 'Enter' || e.key === ' ') {
      props.onClick && props.onClick();
    }
  };

  return /*#__PURE__*/React.createElement(StyledAnchor, _extends({}, props, {
    id: id,
    size: props.size,
    colors: colorConfig,
    type: props.type,
    viewport: props.viewport,
    disabled: props.disabled,
    inverted: props.inverted,
    href: props.href,
    color: props.color,
    bold: props.bold,
    "aria-label": newAriaLabel,
    role: role,
    "aria-disabled": props.disabled,
    tabIndex: props.disabled ? -1 : tabIndex ? tabIndex : 0,
    focusState: focusState,
    bodySmall: bodySmall,
    bodyLarge: bodyLarge,
    activeState: activeState,
    typescale: typescale,
    "data-loc": dataLoc,
    onKeyDown: handleKeyDown
  }), hitArea && /*#__PURE__*/React.createElement(HitArea, {
    type: props.type,
    tabIndex: -1,
    "aria-hidden": true
  }), /*#__PURE__*/React.createElement(Wrapper, {
    colors: props.color,
    tabIndex: -1,
    "aria-hidden": true,
    type: props.type,
    bodyLarge: bodyLarge
  }, props.children));
};

TextLink.defaultProps = defaultProps;
TextLink.propTypes = propTypes;
export default withVDSManager(TextLink);